import React, { useState, useEffect } from 'react';
import { templateAPI, tenantAPI, branchAPI } from '../../services/api';
import { templateUploadAPI } from '../../services/uploadAPI';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import { FileText, Plus, Edit, Trash2, Eye, Image, Building2, ListChecks, Calendar, Palette, Type, X } from 'lucide-react';
import './Templates.css';
import { formatDate } from '../../utils/dateFormat';

const Templates = () => {
    const { selectedBranch } = useBranchStore();
    const navigate = useNavigate();
    const { canView: canViewLandingPages } = usePermission('landing_pages');
    const [templates, setTemplates] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [businesses, setBusinesses] = useState([]);
    const [branches, setBranches] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        tenantId: '',
        branchId: '',
        loginMethods: [],
        pmsProvider: 'generic',
        themePreset: 'custom',
        design: {
            logo: { url: '' },
            partnerBanner: { url: '' },
            backgroundImage: { url: '' },
            footerStrip: { url: '' },
            fontName: 'Inter',
            heading1: { text: 'Connect to WiFi', color: '#a855f7' },
            heading2: { text: 'Please enter details below', color: '#6b7280' },
            headerTitle: '',
            button: { color: '#a855f7', textColor: '#ffffff', text: 'Connect' }
        },
        fields: []
    });

    const availableLoginMethods = [
        { value: 'OTP', label: 'OTP (Mobile)', icon: '📱' },
        { value: 'VOUCHER', label: 'Voucher Code', icon: '🎟️' },
        { value: 'MAC', label: 'MAC Bypass', icon: '💻' },
        { value: 'SOCIAL', label: 'Social Login', icon: '🌐' },
        { value: 'PMS', label: 'PMS / Hotel', icon: '🏨' },
    ];

    const availableFields = [
        { name: 'firstName', label: 'First Name', type: 'text' },
        { name: 'lastName', label: 'Last Name', type: 'text' },
        { name: 'email', label: 'Email', type: 'email' },
        { name: 'phone', label: 'Phone', type: 'tel' },
        { name: 'company', label: 'Company', type: 'text' },
        { name: 'address', label: 'Address', type: 'text' }
    ];

    useEffect(() => {
        fetchTemplates();
        fetchBusinesses();
        fetchBranches();
    }, [selectedBranch]);

    const fetchBranches = async () => {
        try {
            const response = await branchAPI.getAll();
            setBranches(response.data.data.branches || []);
        } catch (error) {
            console.error('Failed to fetch branches:', error);
        }
    };

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const params = selectedBranch ? { branchId: selectedBranch._id } : undefined;
            const response = await templateAPI.getAll(params);
            setTemplates(response.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch templates');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBusinesses = async () => {
        try {
            const response = await tenantAPI.getAll();
            setBusinesses(response.data.data.tenants || []);
        } catch (error) {
            console.error('Failed to fetch businesses:', error);
        }
    };

    const handleFieldToggle = (field) => {
        const exists = formData.fields.find(f => f.name === field.name);

        if (exists) {
            setFormData({
                ...formData,
                fields: formData.fields.filter(f => f.name !== field.name)
            });
        } else {
            setFormData({
                ...formData,
                fields: [
                    ...formData.fields,
                    { ...field, required: true, order: formData.fields.length }
                ]
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingTemplate) {
                await templateAPI.update(editingTemplate._id, formData);
                toast.success('Template updated successfully');
            } else {
                await templateAPI.create(formData);
                toast.success('Template created successfully');
            }

            setShowModal(false);
            setEditingTemplate(null);
            resetForm();
            fetchTemplates();
        } catch (error) {
            toast.error(editingTemplate ? 'Failed to update template' : 'Failed to create template');
            console.error(error);
        }
    };

    const handleEdit = (template) => {
        setEditingTemplate(template);
        setFormData({
            name: template.name,
            tenantId: template.tenantId?._id || '',
            branchId: template.branchId?._id || template.branchId || '',
            loginMethods: template.loginMethods || [],
            pmsProvider: template.pmsProvider || 'generic',
            themePreset: template.themePreset || 'custom',
            design: {
                ...template.design,
                logo: template.design?.logo || { url: '' },
                partnerBanner: template.design?.partnerBanner || { url: '' },
                backgroundImage: template.design?.backgroundImage || { url: '' },
                footerStrip: template.design?.footerStrip || { url: '' },
                headerTitle: template.design?.headerTitle || ''
            },
            fields: template.fields || []
        });
        setShowModal(true);
    };

    const handleDelete = (templateId) => {
        setTemplateToDelete(templateId);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!templateToDelete) return;
        try {
            await templateAPI.delete(templateToDelete);
            toast.success('Template deleted successfully');
            fetchTemplates();
            setShowDeleteModal(false);
            setTemplateToDelete(null);
        } catch (error) {
            toast.error('Failed to delete template');
            console.error(error);
        }
    };

    const handleNewTemplate = () => {
        setEditingTemplate(null);
        resetForm();
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            tenantId: '',
            branchId: selectedBranch?._id || '',
            loginMethods: [],
            pmsProvider: 'generic',
            themePreset: 'custom',
            design: {
                logo: { url: '' },
                partnerBanner: { url: '' },
                backgroundImage: { url: '' },
                footerStrip: { url: '' },
                fontName: 'Inter',
                heading1: { text: 'Connect to WiFi', color: '#a855f7' },
                heading2: { text: 'Please enter details below', color: '#6b7280' },
                button: { color: '#a855f7', textColor: '#ffffff', text: 'Connect' }
            },
            fields: []
        });
    };

    const handleLoginMethodToggle = (method) => {
        const methods = formData.loginMethods.includes(method)
            ? formData.loginMethods.filter(m => m !== method)
            : [...formData.loginMethods, method];
        setFormData({ ...formData, loginMethods: methods });
    };

    const handleLogoUpload = (e) => handleDesignImageUpload(e, 'logo', 'Logo');

    const handleDesignImageUpload = async (e, key, label) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Please select a valid image file');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Image size should be less than 2MB');
            return;
        }

        // Editing an existing (already-saved) template — use the real
        // upload endpoint, stored as a file on the server instead of a
        // giant base64 string embedded in the document.
        if (editingTemplate?._id) {
            try {
                const res = await templateUploadAPI.uploadDesignImage(editingTemplate._id, key, file);
                setFormData(prev => ({ ...prev, design: { ...prev.design, [key]: { url: res.data.data.url } } }));
                toast.success(`${label} uploaded successfully`);
            } catch (err) {
                toast.error(err.response?.data?.message || `Failed to upload ${label.toLowerCase()}`);
            }
            return;
        }

        // Brand-new, not-yet-saved template — no id to attach an upload to
        // yet, so preview as base64 for now; save the template first to
        // switch this image over to a real uploaded file on the next edit.
        const reader = new FileReader();
        reader.onloadend = () => {
            setFormData(prev => ({
                ...prev,
                design: { ...prev.design, [key]: { url: reader.result } }
            }));
            toast.success(`${label} set (save the template to finish uploading)`);
        };
        reader.onerror = () => {
            toast.error('Failed to read image file');
        };
        reader.readAsDataURL(file);
    };

    const filteredTemplates = templates.filter(template =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (template.tenantId?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Stats
    const stats = {
        total: templates.length,
        global: templates.filter(t => !t.tenantId).length,
        custom: templates.filter(t => t.tenantId).length,
    };

    return (
        <div className="templates-page">
            {/* Header */}
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <FileText className="title-icon" />
                            Portal Templates
                        </h1>
                        <p className="page-subtitle-modern">
                            Design and customize your WiFi splash page templates
                        </p>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                        {canViewLandingPages && (
                            <button onClick={() => navigate('/landing-pages')} className="btn-secondary-modern">
                                <Palette size={20} />
                                Visual Page Builder
                            </button>
                        )}
                        <button onClick={handleNewTemplate} className="btn-primary-modern">
                            <Plus size={20} />
                            New Template
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <FileText size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">{stats.total}</div>
                        <div className="stat-label">Total Templates</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon purple">
                        <Palette size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">{stats.custom}</div>
                        <div className="stat-label">Custom Templates</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon green">
                        <Building2 size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">{stats.global}</div>
                        <div className="stat-label">Global Templates</div>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="filters-section">
                <div className="search-box-modern">
                    <i className="fas fa-search"></i>
                    <input
                        type="text"
                        placeholder="Search templates by name or business..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Templates Grid */}
            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading templates...</p>
                </div>
            ) : filteredTemplates.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon">
                        <FileText size={40} />
                    </div>
                    <h3>No Templates Found</h3>
                    <p className="text-gray-600">
                        {searchTerm
                            ? 'No templates match your search. Try a different query.'
                            : 'Create your first portal template to customize the WiFi splash page experience.'}
                    </p>

                </div>
            ) : (
                <div className="templates-grid">
                    {filteredTemplates.map((template) => (
                        <div key={template._id} className="template-card">
                            {/* Preview Area */}
                            <div className="template-preview">
                                {template.design?.logo?.url ? (
                                    <img src={template.design.logo.url} alt="Logo" className="template-logo" />
                                ) : (
                                    <div className="template-logo-placeholder">
                                        <Image size={32} />
                                    </div>
                                )}
                                <div
                                    className="template-heading"
                                    style={{ color: template.design?.heading1?.color || '#a855f7' }}
                                >
                                    {template.design?.heading1?.text || 'Heading'}
                                </div>
                                <button
                                    className="template-button-preview"
                                    style={{
                                        backgroundColor: template.design?.button?.color || '#a855f7',
                                        color: template.design?.button?.textColor || '#fff'
                                    }}
                                >
                                    {template.design?.button?.text || 'Connect'}
                                </button>
                            </div>

                            {/* Template Info */}
                            <div className="template-info">
                                <h3 className="template-name">{template.name}</h3>

                                <div className="template-details">
                                    <div className="detail-item">
                                        <Building2 size={14} />
                                        <span>{template.tenantId?.name || 'Global Template'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <Building2 size={14} />
                                        <span>{template.branchId?.name || 'All Branches'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <ListChecks size={14} />
                                        <span>{template.fields?.length || 0} fields</span>
                                    </div>
                                    <div className="detail-item">
                                        <Type size={14} />
                                        <span>
                                            {template.loginMethods?.length
                                                ? template.loginMethods.join(', ')
                                                : 'No methods set'}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <Calendar size={14} />
                                        <span>{formatDate(template.createdAt)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="template-actions">
                                <button
                                    onClick={() => handleEdit(template)}
                                    className="action-btn edit"
                                    title="Edit template"
                                >
                                    <Edit size={16} />
                                </button>
                                <button
                                    onClick={() => handleDelete(template._id)}
                                    className="action-btn delete"
                                    title="Delete template"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{editingTemplate ? 'Edit Template' : 'Create New Template'}</h2>
                            <button onClick={() => setShowModal(false)} className="modal-close">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    {/* Template Name */}
                                    <div className="form-group-modern full-width">
                                        <label>Template Name *</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Enter template name"
                                            required
                                        />
                                    </div>

                                    {/* Tenant/Business */}
                                    <div className="form-group-modern full-width">
                                        <label>Business (Optional)</label>
                                        <select
                                            value={formData.tenantId}
                                            onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                                        >
                                            <option value="">Global Template</option>
                                            {businesses.map(business => (
                                                <option key={business._id} value={business._id}>
                                                    {business.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Branch */}
                                    <div className="form-group-modern full-width">
                                        <label>Branch (Optional)</label>
                                        <select
                                            value={formData.branchId}
                                            onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                                        >
                                            <option value="">All Branches</option>
                                            {branches.map(branch => (
                                                <option key={branch._id} value={branch._id}>
                                                    {branch.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Logo Upload */}
                                    <div className="form-group-modern full-width">
                                        <label>Logo Image</label>
                                        <div className="logo-upload-section">
                                            {formData.design.logo.url ? (
                                                <div className="logo-preview-container">
                                                    <img
                                                        src={formData.design.logo.url}
                                                        alt="Logo preview"
                                                        className="logo-preview-image"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            design: { ...formData.design, logo: { url: '' } }
                                                        })}
                                                        className="logo-remove-btn"
                                                        title="Remove logo"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="logo-upload-placeholder">
                                                    <Image size={32} />
                                                    <span>No logo uploaded</span>
                                                </div>
                                            )}

                                            <div className="logo-upload-options">
                                                <label className="file-upload-btn">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleLogoUpload}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <i className="fas fa-upload"></i>
                                                    Upload Image
                                                </label>
                                                <span className="upload-or">or</span>
                                                <input
                                                    type="url"
                                                    value={formData.design.logo.url.startsWith('data:') ? '' : formData.design.logo.url}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, logo: { url: e.target.value } }
                                                    })}
                                                    placeholder="Enter image URL"
                                                    className="logo-url-input"
                                                />
                                            </div>
                                            <p className="input-hint">
                                                Upload an image file or enter a URL. Max size: 2MB
                                            </p>
                                        </div>
                                    </div>

                                    {/* Partner Banner Upload (e.g. ASR / Oakwood co-brand badge) */}
                                    <div className="form-group-modern full-width">
                                        <label>Partner Banner Image</label>
                                        <div className="logo-upload-section">
                                            {formData.design.partnerBanner.url ? (
                                                <div className="logo-preview-container">
                                                    <img
                                                        src={formData.design.partnerBanner.url}
                                                        alt="Partner banner preview"
                                                        className="logo-preview-image"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            design: { ...formData.design, partnerBanner: { url: '' } }
                                                        })}
                                                        className="logo-remove-btn"
                                                        title="Remove banner"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="logo-upload-placeholder">
                                                    <Image size={32} />
                                                    <span>No banner uploaded</span>
                                                </div>
                                            )}

                                            <div className="logo-upload-options">
                                                <label className="file-upload-btn">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleDesignImageUpload(e, 'partnerBanner', 'Partner banner')}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <i className="fas fa-upload"></i>
                                                    Upload Image
                                                </label>
                                                <span className="upload-or">or</span>
                                                <input
                                                    type="url"
                                                    value={formData.design.partnerBanner.url.startsWith('data:') ? '' : formData.design.partnerBanner.url}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, partnerBanner: { url: e.target.value } }
                                                    })}
                                                    placeholder="Enter image URL"
                                                    className="logo-url-input"
                                                />
                                            </div>
                                            <p className="input-hint">
                                                Shown above the login form (e.g. discoverasr.com / Oakwood co-brand badge). Upload an image file or enter a URL. Max size: 2MB
                                            </p>
                                        </div>
                                    </div>

                                    {/* Background Image */}
                                    <div className="form-group-modern full-width">
                                        <label>Background Image</label>
                                        <div className="logo-upload-section">
                                            {formData.design.backgroundImage.url ? (
                                                <div className="logo-preview-container">
                                                    <img
                                                        src={formData.design.backgroundImage.url}
                                                        alt="Background preview"
                                                        className="logo-preview-image"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            design: { ...formData.design, backgroundImage: { url: '' } }
                                                        })}
                                                        className="logo-remove-btn"
                                                        title="Remove background"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="logo-upload-placeholder">
                                                    <Image size={32} />
                                                    <span>No background image uploaded</span>
                                                </div>
                                            )}

                                            <div className="logo-upload-options">
                                                <label className="file-upload-btn">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleDesignImageUpload(e, 'backgroundImage', 'Background image')}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <i className="fas fa-upload"></i>
                                                    Upload Image
                                                </label>
                                                <span className="upload-or">or</span>
                                                <input
                                                    type="url"
                                                    value={formData.design.backgroundImage.url.startsWith('data:') ? '' : formData.design.backgroundImage.url}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, backgroundImage: { url: e.target.value } }
                                                    })}
                                                    placeholder="Enter image URL"
                                                    className="logo-url-input"
                                                />
                                            </div>
                                            <p className="input-hint">
                                                Full-page hero/background photo behind the login card. Upload an image file or enter a URL. Max size: 2MB
                                            </p>
                                        </div>
                                    </div>

                                    {/* Footer Brand Strip Image */}
                                    <div className="form-group-modern full-width">
                                        <label>Footer Brand Strip Image</label>
                                        <div className="logo-upload-section">
                                            {formData.design.footerStrip.url ? (
                                                <div className="logo-preview-container">
                                                    <img
                                                        src={formData.design.footerStrip.url}
                                                        alt="Footer strip preview"
                                                        className="logo-preview-image"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            design: { ...formData.design, footerStrip: { url: '' } }
                                                        })}
                                                        className="logo-remove-btn"
                                                        title="Remove footer strip"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="logo-upload-placeholder">
                                                    <Image size={32} />
                                                    <span>No footer strip uploaded</span>
                                                </div>
                                            )}

                                            <div className="logo-upload-options">
                                                <label className="file-upload-btn">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleDesignImageUpload(e, 'footerStrip', 'Footer strip')}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <i className="fas fa-upload"></i>
                                                    Upload Image
                                                </label>
                                                <span className="upload-or">or</span>
                                                <input
                                                    type="url"
                                                    value={formData.design.footerStrip.url.startsWith('data:') ? '' : formData.design.footerStrip.url}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        design: { ...formData.design, footerStrip: { url: e.target.value } }
                                                    })}
                                                    placeholder="Enter image URL"
                                                    className="logo-url-input"
                                                />
                                            </div>
                                            <p className="input-hint">
                                                Row of partner/brand logos shown below the login card on the live portal (e.g. Ascott, Citadines, Oakwood strip). Upload an image file or enter a URL. Max size: 2MB
                                            </p>
                                        </div>
                                    </div>

                                    {/* Heading 1 — optional. Leave blank to auto-generate "WELCOME TO
                                        {Branch Name}" from each branch's own name. This is what lets ONE
                                        shared template serve a multi-property brand (e.g. Oakwood Pune,
                                        Oakwood Mumbai, Oakwood Bangalore) without duplicating the template
                                        per city — just point every property's Branch at this same template
                                        and name each branch "Oakwood Pune", "Oakwood Mumbai", etc. */}
                                    <div className="form-group-modern full-width">
                                        <label>Heading 1 Text <span style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 400 }}>(optional)</span></label>
                                        <input
                                            type="text"
                                            value={formData.design.heading1.text}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                design: {
                                                    ...formData.design,
                                                    heading1: { ...formData.design.heading1, text: e.target.value }
                                                }
                                            })}
                                            placeholder="Leave blank to auto-use the branch name"
                                        />
                                        <p className="input-hint">
                                            Leave blank to automatically show "WELCOME TO &#123;Branch Name&#125;" using each branch's own name. Useful when one brand has multiple properties (e.g. Oakwood Pune, Oakwood Mumbai) sharing this same template — each property's portal will show its own city automatically instead of you typing a heading per property. Fill this in only if you want one fixed heading regardless of branch.
                                        </p>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Heading 1 Color</label>
                                        <input
                                            type="color"
                                            value={formData.design.heading1.color}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                design: {
                                                    ...formData.design,
                                                    heading1: { ...formData.design.heading1, color: e.target.value }
                                                }
                                            })}
                                        />
                                    </div>

                                    {/* Heading 2 */}
                                    <div className="form-group-modern">
                                        <label>Heading 2 Text</label>
                                        <input
                                            type="text"
                                            value={formData.design.heading2.text}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                design: {
                                                    ...formData.design,
                                                    heading2: { ...formData.design.heading2, text: e.target.value }
                                                }
                                            })}
                                        />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Heading 2 Color</label>
                                        <input
                                            type="color"
                                            value={formData.design.heading2.color}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                design: {
                                                    ...formData.design,
                                                    heading2: { ...formData.design.heading2, color: e.target.value }
                                                }
                                            })}
                                        />
                                    </div>

                                    {/* Button */}
                                    <div className="form-group-modern">
                                        <label>Button Text *</label>
                                        <input
                                            type="text"
                                            value={formData.design.button.text}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                design: {
                                                    ...formData.design,
                                                    button: { ...formData.design.button, text: e.target.value }
                                                }
                                            })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Button Color</label>
                                        <input
                                            type="color"
                                            value={formData.design.button.color}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                design: {
                                                    ...formData.design,
                                                    button: { ...formData.design.button, color: e.target.value }
                                                }
                                            })}
                                        />
                                    </div>

                                    {/* Allowed Login Methods */}
                                    <div className="form-group-modern full-width">
                                        <label>Allowed Login Methods</label>
                                        <div className="login-methods-selector">
                                            {availableLoginMethods.map(method => (
                                                <label key={method.value} className={`method-checkbox ${formData.loginMethods.includes(method.value) ? 'checked' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.loginMethods.includes(method.value)}
                                                        onChange={() => handleLoginMethodToggle(method.value)}
                                                    />
                                                    <span className="method-icon">{method.icon}</span>
                                                    <span>{method.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <p className="input-hint">Select which login methods are available on this portal template.</p>
                                    </div>

                                    {/* PMS Provider (only when PMS / Hotel login method enabled) */}
                                    {formData.loginMethods.includes('PMS') && (
                                        <div className="form-group-modern full-width">
                                            <label>PMS Provider</label>
                                            <select
                                                className="input-modern"
                                                value={formData.pmsProvider || 'generic'}
                                                onChange={(e) => setFormData({ ...formData, pmsProvider: e.target.value })}
                                            >
                                                <option value="generic">Generic (Room Number + Last Name)</option>
                                                <option value="capitaland">CapitaLand ODX (ASR Member / Room Number)</option>
                                                <option value="wincloud">WinCloud PMS</option>
                                                <option value="winhms">WinHMS</option>
                                                <option value="idsnext">IDS Next</option>
                                            </select>
                                            <p className="input-hint">Determines the guest login fields shown on the portal (e.g. CapitaLand shows the combined ASR Member / Room Number login).</p>
                                        </div>
                                    )}

                                    {/* Theme Preset — available for any template, not just CapitaLand. "Oakwood
                                        ASR" is a reusable hero-photo + side-panel layout: on the actual
                                        CapitaLand/Oakwood property it uses bundled Ascott assets and copy with
                                        zero uploads needed; on any other property it pulls the hero photo from
                                        Background Image, the header banner from Logo, and the footer strip from
                                        Footer Strip below (upload at least a Background Image for it to look
                                        right — without one the hero section is simply left blank). */}
                                    <div className="form-group-modern full-width">
                                        <label>Theme Preset</label>
                                        <select
                                            className="input-modern"
                                            value={formData.themePreset || 'custom'}
                                            onChange={(e) => setFormData({ ...formData, themePreset: e.target.value })}
                                        >
                                            <option value="custom">Custom (use the image/color fields below)</option>
                                            <option value="oakwood-asr">Oakwood ASR — hero photo + side login panel layout</option>
                                        </select>
                                        <p className="input-hint">
                                            {formData.pmsProvider === 'capitaland'
                                                ? '"Oakwood ASR" uses a fixed, ready-made design (hero photo, ASR/Oakwood banner, brand strip, teal/gold colors) bundled directly into the app — no need to upload images below.'
                                                : '"Oakwood ASR" reuses the same hero-photo + side-panel layout for this property. Upload a Background Image (hero photo) and optionally a Logo (header banner) and Footer Strip below — guests will log in with Room Number + Last Name.'}
                                        </p>
                                    </div>

                                    {/* Header Title — only relevant for Oakwood ASR theme. Overrides the
                                        top-right banner with plain text (e.g. "MATHERAN") instead of the fixed
                                        Ascott/Oakwood banner image or an uploaded Logo image. Lets a property
                                        keep the exact same Oakwood photo/footer/styling while still showing
                                        its own name in that one spot — useful when pmsProvider is left as
                                        CapitaLand purely to reuse the bundled look-and-feel. */}
                                    {formData.themePreset === 'oakwood-asr' && (
                                        <div className="form-group-modern full-width">
                                            <label>Header Title <span style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 400 }}>(optional)</span></label>
                                            <input
                                                type="text"
                                                className="input-modern"
                                                value={formData.design.headerTitle || ''}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    design: { ...formData.design, headerTitle: e.target.value }
                                                })}
                                                placeholder="e.g. MATHERAN"
                                            />
                                            <p className="input-hint">
                                                Replaces the top-right header banner with this text instead of the Ascott/Oakwood image (or your uploaded Logo). Leave blank to keep the default banner image.
                                            </p>
                                        </div>
                                    )}

                                    {/* Fields Section */}
                                    <div className="form-group-modern full-width">
                                        <label>Form Fields</label>
                                        <div className="fields-selector">
                                            {availableFields.map(field => (
                                                <label key={field.name} className="field-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.fields.some(f => f.name === field.name)}
                                                        onChange={() => handleFieldToggle(field)}
                                                    />
                                                    <span>{field.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Preview Toggle */}
                                <div className="preview-toggle">
                                    <button
                                        type="button"
                                        onClick={() => setShowPreview(!showPreview)}
                                        className="btn-secondary-modern"
                                    >
                                        <Eye size={20} />
                                        {showPreview ? 'Hide Preview' : 'Show Preview'}
                                    </button>
                                </div>

                                {/* Preview */}
                                {showPreview && (
                                    <div className="template-preview-container">
                                        <div
                                            className="template-preview-frame"
                                            style={formData.design.backgroundImage.url ? {
                                                backgroundImage: `url(${formData.design.backgroundImage.url})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center'
                                            } : undefined}
                                        >
                                            {formData.design.partnerBanner.url && (
                                                <img src={formData.design.partnerBanner.url} alt="Partner banner" className="preview-partner-banner" />
                                            )}
                                            {formData.design.logo.url && (
                                                <img src={formData.design.logo.url} alt="Logo" className="preview-logo" />
                                            )}
                                            <h1 style={{ color: formData.design.heading1.color }}>
                                                {formData.design.heading1.text}
                                            </h1>
                                            <p style={{ color: formData.design.heading2.color }}>
                                                {formData.design.heading2.text}
                                            </p>
                                            <div className="preview-fields">
                                                {formData.loginMethods.includes('PMS') && (
                                                    formData.pmsProvider === 'capitaland' ? (
                                                        <>
                                                            <input type="text" placeholder="ASR Member Email OR Room Number" disabled />
                                                            <input type="password" placeholder="ASR Member Password OR Last Name" disabled />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <input type="text" placeholder="Room Number" disabled />
                                                            <input type="text" placeholder="Last Name" disabled />
                                                        </>
                                                    )
                                                )}
                                                {formData.loginMethods.includes('VOUCHER') && (
                                                    <input type="text" placeholder="Voucher Code" disabled />
                                                )}
                                                {formData.loginMethods.includes('OTP') && (
                                                    <input type="tel" placeholder="Mobile Number" disabled />
                                                )}
                                                {formData.fields.map((field) => (
                                                    <input
                                                        key={field.name}
                                                        type={field.type}
                                                        placeholder={`${field.label}${field.required ? '*' : ''}`}
                                                        disabled
                                                    />
                                                ))}
                                            </div>
                                            <button
                                                type="button"
                                                style={{
                                                    backgroundColor: formData.design.button.color,
                                                    color: formData.design.button.textColor
                                                }}
                                                disabled
                                            >
                                                {formData.design.button.text}
                                            </button>
                                            {formData.design.footerStrip.url && (
                                                <img src={formData.design.footerStrip.url} alt="Footer brand strip" className="preview-footer-strip" />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer-modern">
                                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary-modern">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary-modern">
                                    <Plus size={20} />
                                    {editingTemplate ? 'Update Template' : 'Create Template'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <h2>Delete Template</h2>
                            <button onClick={() => setShowDeleteModal(false)} className="modal-close">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="modal-body-modern">
                            <p>Are you sure you want to delete this template? This action cannot be undone.</p>
                        </div>

                        <div className="modal-footer-modern">
                            <button onClick={() => setShowDeleteModal(false)} className="btn-secondary-modern">
                                Cancel
                            </button>
                            <button onClick={confirmDelete} className="btn-danger-modern">
                                <Trash2 size={20} />
                                Delete Template
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Templates;
